import { useState } from "react";
import { Typography, Label, Textarea } from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { UserViewData } from "./testCaseFormTypes";

interface UserViewSectionProps {
  userView: UserViewData | null | undefined;
  onChange: (userView: UserViewData | null) => void;
}

// Format user view as JSON string for display
const formatUserView = (userView: UserViewData | null | undefined): string => {
  if (!userView || Object.keys(userView).length === 0) return "";
  return JSON.stringify(userView, null, 2);
};

export const UserViewSection: React.FC<UserViewSectionProps> = ({ userView, onChange }) => {
  const [jsonText, setJsonText] = useState(() => formatUserView(userView));
  const [jsonError, setJsonError] = useState<string | null>(null);

  const handleJsonChange = (text: string) => {
    setJsonText(text);

    if (!text.trim()) {
      setJsonError(null);
      onChange(null);
      return;
    }

    try {
      const parsed = JSON.parse(text);
      if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
        setJsonError("Must be a JSON object");
        return;
      }
      if (!parsed.entity_type) {
        setJsonError("entity_type is required");
        return;
      }
      setJsonError(null);
      onChange(parsed as UserViewData);
    } catch {
      setJsonError("Invalid JSON format");
    }
  };

  return (
    <Stack direction="column" gap="sm">
      <Typography size="sm" weight="regular">
        Page Context (User View)
      </Typography>
      <Typography size="xs" className="al-text-gray-500">
        Set the page context to simulate where the user is when asking the question. Provide a JSON
        object with entity_type and any additional fields.
      </Typography>

      <div>
        <Label htmlFor="user-view-json" className="al-mb-1 al-block">
          <Typography size="xs" weight="medium">
            Context JSON
          </Typography>
        </Label>
        <Textarea
          id="user-view-json"
          placeholder={`{
  "entity_type": "snowflake_job",
  "workflowId": "my_workflow"
}`}
          className={`al-mt-1 al-font-mono al-text-sm ${jsonError ? "al-border-red-500" : ""}`}
          rows={4}
          value={jsonText}
          onChange={e => handleJsonChange(e.target.value)}
        />
        {jsonError && (
          <Typography size="xs" className="al-text-red-500 al-mt-1">
            {jsonError}
          </Typography>
        )}
        <Typography size="xs" className="al-text-gray-400 al-mt-1">
          Common entity types: snowflake_job, snowflake_jobs_list, dbt_model, dbt_job,
          snowflake_query, snowflake_warehouse
        </Typography>
      </div>
    </Stack>
  );
};
